import { Link } from "gatsby"
import React from "react"
import styled from "styled-components"
import { CTA, Container, Divider, Slab, Title } from "../../components/common"
import { globalStyleVariables } from "../../styles/variables"
import { Wrapper } from "./style"
import {
  Button,
  useDisclosure,
} from "@chakra-ui/react"
import { ChevronDownIcon, ChevronUpIcon } from "@chakra-ui/icons";


const FreeUntilYouAreReady = styled.div`
  width: 100%;

  .content {
    display: flex;
    justify-content: space-between;
    align-items: center;
    .cta-container {
      display: flex;
      gap: 24px;
      @media (${globalStyleVariables.MOBILE_MEDIA_QUERY}) {
        flex-direction: column;
        width: 100%;
      }
    }
    .cta-item {
      margin-left: 24px;
      @media (${globalStyleVariables.MOBILE_MEDIA_QUERY}) {
        margin-left: 0;
        margin-top: 30px;
      }
    }

    @media (${globalStyleVariables.MOBILE_MEDIA_QUERY}) {
      flex-direction: column;
      align-items: flex-start;
      gap: 24px;
    }
  }
`

var defaultGettingStartedText =
  "Free until you’re ready to upgrade to the Pro plan"

const LinkTag = ({
  usingInFrame = false,
  target = "_blank",
  to,
  children,
}) => {
  if (usingInFrame) {
    return (
      <a href={to} target={target}>
        {children}
      </a>
    )
  } else {
    return (
      <Link to={to}>
        {children}
      </Link>
    )
  }
}

const Footer = ({ gettingStartedText = defaultGettingStartedText, primaryCTALink = "/registration", primaryCTAText = "Get started for free", showContactUs = true
  , secondaryCTALink = "/contact-us", secondaryCTAText = "Talk to us", usingInFrame = false }) => {
  const { isOpen, onToggle } = useDisclosure();

  return (
    <Slab bg="dark">
      <Wrapper>
        <Container>
          <FreeUntilYouAreReady>
            <div className="content">
              <Title
                style={{
                  color: "#ffffff",
                  textAlign: "left",
                  margin: "0",
                  maxWidth: "645px",
                }}
              >
                {gettingStartedText}
              </Title>
              <div className="cta-container">
                <CTA
                  colorScheme="imagekitWhite"
                  title={primaryCTAText}
                  link={primaryCTALink}
                  usingInFrame={usingInFrame}
                />

                {showContactUs &&
                  <CTA
                    colorScheme="imagekitWhite"
                    secondary={true}
                    title={secondaryCTAText}
                    link={secondaryCTALink}
                    usingInFrame={usingInFrame}
                  ></CTA>
                }
              </div>
            </div>
            <div className="divider-wrapper">
              <Divider color={"#a8b8d2"} />
            </div>
          </FreeUntilYouAreReady>
          <div className="grid">
            <div className="column">
              <h4>Image and Video API</h4>
              <ul>
                <li>
                  <LinkTag usingInFrame={usingInFrame} target="_blank" to="/image-api">
                    Image API
                  </LinkTag>
                </li>
                <li>
                  <LinkTag usingInFrame={usingInFrame} target="_blank" to="/video-api">
                    Video API
                  </LinkTag>
                </li>
                <li>
                  <LinkTag usingInFrame={usingInFrame} target="_blank" to="/solutions/performance-optimization/">
                    Web Performance Optimization
                  </LinkTag>
                </li>
                <li>
                  <LinkTag usingInFrame={usingInFrame} target="_blank" to="/solutions/media-personalization/">
                    Overlays and Personalization
                  </LinkTag>
                </li>
                <li>
                  <LinkTag usingInFrame={usingInFrame} target="_blank" to="/solutions/media-delivery-solution/">
                    Use with your CDN & Storage
                  </LinkTag>
                </li>
                <li>
                  <LinkTag usingInFrame={usingInFrame} target="_blank" to="/solutions/video-streaming/">
                    Video Streaming
                  </LinkTag>
                </li>
              </ul>
            </div>
            <div className="column">
              <h4>DAM Use Cases</h4>
              <ul>
                <li>
                  <LinkTag usingInFrame={usingInFrame} target="_blank" to="/digital-asset-management/">
                    Centralized DAM
                  </LinkTag>
                </li>
                <li>
                  <LinkTag usingInFrame={usingInFrame} target="_blank" to="/dam-collaboration/">Cross-Team Collaboration</LinkTag>
                </li>
                <li>
                  <LinkTag usingInFrame={usingInFrame} target="_blank" to="/solutions/headless-dam">
                    Headless DAM
                  </LinkTag>
                </li>
                <li>
                  <LinkTag usingInFrame={usingInFrame} target="_blank" to="/solutions/dam-for-nonprofits">
                    DAM for Nonprofits
                  </LinkTag>
                </li>
                <li>
                  <LinkTag usingInFrame={usingInFrame} target="_blank" to="/dam/integration/">
                    DAM integrations
                  </LinkTag>
                </li>
              </ul>
            </div>

            <div className="column">
              <h4>Compare</h4>
              <ul className="list">
                <li>
                  <LinkTag usingInFrame={usingInFrame} target="_blank" to="/cloudinary-alternative">
                    ImageKit vs Cloudinary
                  </LinkTag>
                </li>
                <li>
                  <LinkTag usingInFrame={usingInFrame} target="_blank" to="/imgix-alternative">
                    ImageKit vs Imgix
                  </LinkTag>
                </li>
                <li>
                  <LinkTag usingInFrame={usingInFrame} target="_blank" to="/sirv-alternative">
                    ImageKit vs Sirv
                  </LinkTag>
                </li>
                <li>
                  <LinkTag usingInFrame={usingInFrame} target="_blank" to="/imageengine-alternative/">
                    ImageKit vs ImageEngine
                  </LinkTag>
                </li>
                <li>
                  <LinkTag usingInFrame={usingInFrame} target="_blank" to="/bynder-alternative/">
                    ImageKit vs Bynder
                  </LinkTag>
                </li>
                <li>
                  <LinkTag usingInFrame={usingInFrame} target="_blank" to="/brandfolder-alternative/">
                    ImageKit vs BrandFolder
                  </LinkTag>
                </li>
                {/* Dynamic content toggled by 'View More' button */}
                {isOpen && (
                  <>
                    <li>
                      <LinkTag usingInFrame={usingInFrame} target="_blank" to="/dropbox-alternative/">
                        ImageKit vs Dropbox
                      </LinkTag>
                    </li>
                    <li>
                      <LinkTag usingInFrame={usingInFrame} target="_blank" to="/googledrive-alternative/">
                        ImageKit vs Google Drive
                      </LinkTag>
                    </li>
                    <li>
                      <LinkTag usingInFrame={usingInFrame} target="_blank" to="/adobe-experience-manager-aem-alternative/">
                        ImageKit vs Adobe
                      </LinkTag>
                    </li>
                    <li>
                      <LinkTag usingInFrame={usingInFrame} target="_blank" to="/acquia-alternative/">
                        ImageKit vs Acquia
                      </LinkTag>
                    </li>
                    <li>
                      <LinkTag usingInFrame={usingInFrame} target="_blank" to="/aprimo-alternative/">
                        ImageKit vs Aprimo
                      </LinkTag>
                    </li>
                    <li>
                      <LinkTag usingInFrame={usingInFrame} target="_blank" to="/canto-alternative/">
                        ImageKit vs Canto
                      </LinkTag>
                    </li>
                    <li>
                      <LinkTag usingInFrame={usingInFrame} target="_blank" to="/mediavalet-alternative/">
                        ImageKit vs MediaValet
                      </LinkTag>
                    </li>
                  </>
                )}
                <li>
                  <Button rightIcon={isOpen ? <ChevronUpIcon /> : <ChevronDownIcon />} onClick={onToggle} variant="link" colorScheme="white">
                    {isOpen ? 'View Less' : 'View More'}
                  </Button>
                </li>

              </ul>
            </div>
            <div className="column">
              <h4>Resources</h4>
              <ul className="list">
                <li>
                  <LinkTag usingInFrame={usingInFrame} target="_blank" to="/plans">Pricing</LinkTag>
                </li>
                <li>
                  <LinkTag usingInFrame={usingInFrame} target="_blank" to="/customers">Customers</LinkTag>
                </li>

                <li>
                  <a href="/blog" target="_blank">
                    Blogs
                  </a>
                </li>
                <li>
                  <a href="https://docs.imagekit.io" target="_blank">
                    Documentation
                  </a>
                </li>
                <li>
                  <LinkTag usingInFrame={usingInFrame} to="/imagekit-partner-program" target="_blank">
                    Become a Partner
                  </LinkTag>
                </li>
                <li>
                  <LinkTag usingInFrame={usingInFrame} to="/startup-program" target="_blank">
                    Startup Program
                  </LinkTag>
                </li>

                <li>
                  <LinkTag usingInFrame={usingInFrame} to="/format-converter/" target="_blank">
                    Format converter tool
                  </LinkTag>
                </li>

                <li>
                  <LinkTag usingInFrame={usingInFrame} to="/tools/free-media-hosting/" target="_blank">
                    Free media hosting
                  </LinkTag>
                </li>

                <li>
                  <LinkTag usingInFrame={usingInFrame} to="/tools/remove-background/" target="_blank">
                    Free background remover
                  </LinkTag>
                </li>
              </ul>
            </div>
          </div>
          <div
            className="grid"
            id="logo-badges-grid"
          >
            <div className="column" style={{
              flexGrow: "1",
              maxWidth: "initial"
            }}>
              <LinkTag usingInFrame={usingInFrame} target="_blank" to="/">
                <img
                  src={globalStyleVariables.whiteLogoUrl}
                  width="158"
                  alt="ImageKit.io logo"
                ></img>
              </LinkTag>
            </div>
            <div className="column" style={{
              width: "80px",
              padding: 0,
              marginRight: "10px"
            }}>
              <LinkTag usingInFrame={usingInFrame} target="_blank" to="/security-and-trust">
                <img
                  src={globalStyleVariables.gdprReadyBadge}
                  width="80"
                  alt="GDPR Ready - ImageKit"
                  title="GDPR Ready - ImageKit"
                ></img>
              </LinkTag>
            </div>
            <div className="column" style={{
              width: "80px",
              padding: 0
            }}>
              <LinkTag usingInFrame={usingInFrame} target="_blank" to="/security-and-trust">
                <img
                  src={globalStyleVariables.isoComplianceBadge}
                  width="80"
                  alt="ISO 27001 Compliant - ImageKit"
                  title="ISO 27001 Compliant - ImageKit"
                ></img>
              </LinkTag>
            </div>
          </div>
          <div className="divider-wrapper">
            <Divider color={"#a8b8d2"} />
          </div>
          <div className="copyright-section">
            <div>
              © ImageKit Private Limited {new Date().getFullYear()}
            </div>
            <div className="links">
              <LinkTag usingInFrame={usingInFrame} target="_blank" to="/terms">Terms</LinkTag>
              <LinkTag usingInFrame={usingInFrame} target="_blank" to="/privacy-policy-new">Privacy Policy</LinkTag>
              <LinkTag usingInFrame={usingInFrame} target="_blank" to="/gdpr">GDPR Policy</LinkTag>
              <LinkTag usingInFrame={usingInFrame} target="_blank" to="/security-and-trust">Security and Trust</LinkTag>
              <LinkTag usingInFrame={usingInFrame} target="_blank" to="/about">About Us</LinkTag>
              <LinkTag usingInFrame={usingInFrame} target="_blank" to="/contact-us">Contact Us</LinkTag>
            </div>
            <div className="social-icons">
              {/* Linkedin, twitter and youtube link with images */}
              <a
                href="https://www.linkedin.com/company/imagekit-io/"
                target="_blank"
                rel="noreferrer"
              >
                <img
                  src="https://ik.imagekit.io/ikmedia/website-2024/linkedin.svg?updatedAt=1709641890471"
                  alt="Linkedin"
                />
              </a>
              <a
                href="https://twitter.com/imagekitio?lang=en"
                target="_blank"
                rel="noreferrer"
              >
                <img
                  src="https://ik.imagekit.io/ikmedia/website-2024/twitter.svg?updatedAt=1709641890529"
                  alt="Twitter"
                />
              </a>
              <a
                href="https://www.youtube.com/@imagekit"
                target="_blank"
                rel="noreferrer"
              >
                <img
                  src="https://ik.imagekit.io/ikmedia/website-2024/youtube.svg?updatedAt=1709641890507"
                  alt="Youtube"
                />
              </a>
            </div>
          </div>
        </Container>
      </Wrapper>
    </Slab>
  )
}

export default Footer
